/**
 * cta.js — helpers for the Phase 3.3 conversion architecture.
 *
 * Centralises:
 *   - service-slug → estimate project_type mapping (so ProjectEstimateWizard
 *     can preselect a project type from a service page slug).
 *   - the tiny session-scoped tracker used to associate a case-study view
 *     with a later conversion ("assisted by case study").
 *   - the dispatchers used by CommercialCTA so every service page, case
 *     study and hub opens the SAME central estimate wizard with the SAME
 *     context shape.
 */

// ----- service slug → estimate project_type (must match PROJECT_TYPES in wizard)
const SERVICE_TO_PROJECT_TYPE = {
  "ai-development": "ai",
  "ai-agent-development": "ai",
  "custom-software-development": "unsure",
  "saas-development": "web",
  "crm-development": "crm",
  "mobile-app-development": "mobile",
  "web-application-development": "web",
  "edtech-development": "web",
  "mvp-development": "web",
  "software-modernisation": "upgrade",
};
export function serviceSlugToProjectType(slug) {
  if (!slug) return null;
  return SERVICE_TO_PROJECT_TYPE[slug] || null;
}

// ----- session-scoped assisted-conversion tracker (first-party only)
const LAST_CASE_STUDY_KEY = "clw:lastCaseStudy";
export function rememberCaseStudyView(slug) {
  if (typeof window === "undefined" || !slug) return;
  try {
    window.sessionStorage.setItem(
      LAST_CASE_STUDY_KEY,
      JSON.stringify({ slug, ts: Date.now() }),
    );
  } catch { /* sessionStorage may be blocked */ }
}
export function getLastCaseStudyView() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_CASE_STUDY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.slug ? parsed : null;
  } catch { return null; }
}

// ----- dispatchers ---------------------------------------------------------
/**
 * Open the central ProjectEstimateWizard with source/service/case-study
 * context. All fields are optional. `serviceSlug` will be mapped to
 * a project_type where possible.
 */
export function openEstimateWizard({
  serviceSlug = null,
  serviceName = null,
  caseStudySlug = null,
  caseStudyName = null,
  marketSlug = null,
  sourcePage = typeof window !== "undefined" ? window.location.pathname : null,
  sourceType = null,
  ctaLocation = null,
} = {}) {
  if (typeof window === "undefined") return;
  const detail = {
    presetType: serviceSlugToProjectType(serviceSlug),
    presetService: serviceSlug,
    presetServiceName: serviceName,
    caseStudySlug,
    caseStudyName,
    marketSlug,
    sourcePage,
    sourceType,
    ctaLocation,
    source: sourceType || "cta",
  };
  window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail }));
}

/**
 * Route the user to Contact prefilled with a 15-minute-consultation intent,
 * preserving service / case-study context via query parameters. The Contact
 * page reads these on mount to prefill the subject and message.
 */
export function buildConsultationHref({
  serviceSlug = null,
  serviceName = null,
  caseStudySlug = null,
  caseStudyName = null,
  marketSlug = null,
} = {}) {
  const params = new URLSearchParams({ subject: "15-min-consultation" });
  if (serviceSlug) params.set("service", serviceSlug);
  if (serviceName) params.set("service_name", serviceName);
  if (caseStudySlug) params.set("case_study", caseStudySlug);
  if (caseStudyName) params.set("case_study_name", caseStudyName);
  if (marketSlug) params.set("market", marketSlug);
  return `/contact?${params.toString()}`;
}
